<template>
  <funding-base :state="state" :current-funding-pot-id="state.funding_pot.id">
    <div
      v-if="$page.stream_counts.length > 1 && $page.competition_streams.length > 1"
      class="border-b border-grey-300 py-2"
    >
      <nav class="navigation container">
        <ul class="flex items-center justify-center">
          <li class="navigation-item">
            <a
              :href="route(state.route, state.funding_pot.id)"
              :class="!state.stream ? 'is-active' : null"
            >
              All Streams
            </a>
          </li>
          <li
            v-for="stream in $page.stream_counts"
            :key="stream.key"
            class="navigation-item"
          >
            <a
              :href="
                route(state.route, {
                  funding: state.funding_pot.id,
                  stream: stream.key || 'unknown'
                })
              "
              class="mx-4"
              :class="isActive(stream.key || 'unknown') ? 'is-active' : null"
            >
              {{ stream.name }} ({{ stream.count }})
            </a>
          </li>
        </ul>
      </nav>
    </div>
    <template v-if="applications.length">
      <dashboard-section
        v-for="group in groupedApplications"
        :key="group.title"
      >
        <template #title>
          <h2 class="text-xl mb-2 pr-6 mr-6 border-r">
            {{ group.title }}
          </h2>
          <p class="pb-1">{{ group.applications.length }} Applications</p>
        </template>
        <ul class="list-reset mt-4">
          <li v-for="application in group.applications" :key="application.uuid">
            <funding-competition-submission
              :group="{
                status: group.status,
                icon: group.icon,
                badge: group.badge
              }"
              :reject-keys="rejectKeys"
              :application="application"
              :funding-pot="state.funding_pot.id"
            />
          </li>
        </ul>
      </dashboard-section>
    </template>
    <template v-else>
      <div class="container mt-12">
        <div
          class="flex border-4 border-dashed py-6 items-center flex-col justify-center"
        >
          <h1>No {{ competition.type.name }}s Found</h1>
          <div v-if="hasStreamFilter" class="mt-6 mb-4">
            <app-button
              class="px-6"
              tag="a"
              :href="route('funding.applications.index', state.funding_pot.id)"
            >
              View all {{ competition.type.name }}s
            </app-button>
          </div>
        </div>
      </div>
    </template>
  </funding-base>
</template>

<script>
import { isBefore, parseISO } from "date-fns";

export default {
  name: "FundingCompetitionIndex",
  methods: {
    isActive(stream) {
      return this.state.stream === stream;
    }
  },
  computed: {
    hasStreamFilter() {
      return document.location.search.includes("stream=");
    },
    competition() {
      return this.state.competition ?? [];
    },
    rejectKeys() {
      return this.state?.reject_keys ?? null;
    },
    applications() {
      if (!this.state.competition) return [];
      return this.competition.applications.map(application => {
        application.type = this.state.competition.type.name;
        return application;
      });
    },
    invitedApplications() {
      return this.applications.filter(application => {
        if (!application.rejected_at && application.invited_at)
          return application;
      });
    },
    rejectedApplications() {
      return this.applications.filter(application => {
        if (application.rejected_at) return application;
      });
    },
    submittedApplications() {
      return this.applications.filter(application => {
        if (
          application.submitted_at &&
          !application.rejected_at &&
          !application.invited_at
        )
          return application;
      });
    },
    draftApplications() {
      return this.applications.filter(application => {
        if (
          !application.submitted_at &&
          application.application_progress_percentage > 0
        )
          return application;
      });
    },
    notStartedApplications() {
      return this.applications.filter(application => {
        if (application.application_progress_percentage == 0)
          return application;
      });
    },
    groupedApplications() {
      const applicationGroups = [];
      if (this.submittedApplications.length) {
        applicationGroups.push({
          title: "Submitted",
          status: "positive",
          icon: "check",
          badge: "Submitted",
          applications: this.submittedApplications
        });
      }
      if (this.draftApplications.length) {
        applicationGroups.push({
          title: this.applicationOpen ? "Draft" : "Expired",
          status: this.applicationOpen ? "neutral" : "grey",
          icon: "time",
          badge: "Draft",
          applications: this.draftApplications
        });
      }
      if (this.invitedApplications.length) {
        applicationGroups.push({
          title: "Approved",
          status: "positive",
          icon: "check",
          badge: "Approved",
          applications: this.invitedApplications
        });
      }
      if (this.rejectedApplications.length) {
        applicationGroups.push({
          title: "Rejected",
          status: "negative",
          icon: "close-circle",
          badge: "Rejected",
          applications: this.rejectedApplications
        });
      }
      if (this.notStartedApplications.length) {
        applicationGroups.push({
          title: `${this.competition.type.name} Not Started`,
          status: "grey",
          icon: "close-circle",
          badge: "Not Started",
          applications: this.notStartedApplications
        });
      }
      return applicationGroups;
    },
    applicationOpen() {
      return isBefore(
        new Date(),
        parseISO(this.competition.application_end_date)
      );
    }
  }
};
</script>
